import React from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Bell, 
  BellOff, 
  Volume2, 
  VolumeX, 
  MessageSquare, 
  Briefcase, 
  CheckSquare, 
  AlertCircle,
  Shield,
  Smartphone
} from 'lucide-react';
import { toast } from 'sonner';

export default function NotificationSettings() {
  const { 
    permission, 
    isSupported, 
    preferences, 
    updatePreferences,
    requestPermission,
    showPushNotification
  } = useNotifications();

  const handleRequestPermission = async () => {
    const result = await requestPermission();
    if (result === 'granted') {
      toast.success('Push notifications enabled!');
      // Send test notification
      showPushNotification('Notifications Enabled', {
        body: 'You will now receive push notifications from LEAP Pro',
      });
    } else if (result === 'denied') {
      toast.error('Permission denied. Please enable in browser settings.');
    }
  };

  const handleTestNotification = () => {
    showPushNotification('Test Notification', {
      body: 'This is a test notification from LEAP Pro',
      tag: 'test-' + Date.now(),
    });
    toast.success('Test notification sent!');
  };

  return (
    <div className="container mx-auto p-6 max-w-3xl" data-testid="notification-settings-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Notification Settings</h1>
        <p className="text-gray-500 mt-1">Manage how you receive notifications</p>
      </div>

      {/* Permission Status Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Receive notifications even when the app is minimized
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isSupported ? (
            <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">Not Supported</p>
                <p className="text-sm text-yellow-600">
                  Your browser doesn't support push notifications
                </p>
              </div>
            </div>
          ) : permission === 'granted' ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Bell className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Push Notifications Enabled</p>
                  <p className="text-sm text-gray-500">
                    You'll receive desktop notifications
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Active
              </Badge>
            </div>
          ) : permission === 'denied' ? (
            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
              <BellOff className="h-5 w-5 text-red-600" />
              <div className="flex-1">
                <p className="font-medium text-red-800">Notifications Blocked</p>
                <p className="text-sm text-red-600">
                  Please enable notifications in your browser settings
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <BellOff className="h-5 w-5 text-gray-500" />
                </div>
                <div>
                  <p className="font-medium">Push Notifications Disabled</p>
                  <p className="text-sm text-gray-500">
                    Click to enable desktop notifications
                  </p>
                </div>
              </div>
              <Button onClick={handleRequestPermission}>
                Enable
              </Button>
            </div>
          )}

          {permission === 'granted' && (
            <div className="mt-4 pt-4 border-t">
              <Button variant="outline" size="sm" onClick={handleTestNotification}>
                Send Test Notification
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Notification Preferences
          </CardTitle>
          <CardDescription>
            Choose which notifications you want to receive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Master Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {preferences.pushEnabled ? (
                <Bell className="h-5 w-5 text-blue-600" />
              ) : (
                <BellOff className="h-5 w-5 text-gray-400" />
              )}
              <div>
                <Label htmlFor="push-enabled" className="font-medium">
                  Enable Push Notifications
                </Label>
                <p className="text-sm text-gray-500">
                  Master toggle for all push notifications
                </p>
              </div>
            </div>
            <Switch
              id="push-enabled"
              checked={preferences.pushEnabled}
              onCheckedChange={(checked) => updatePreferences({ pushEnabled: checked })}
            />
          </div>

          {/* Sound Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {preferences.soundEnabled ? (
                <Volume2 className="h-5 w-5 text-blue-600" />
              ) : (
                <VolumeX className="h-5 w-5 text-gray-400" />
              )}
              <div>
                <Label htmlFor="sound-enabled" className="font-medium">
                  Notification Sounds
                </Label>
                <p className="text-sm text-gray-500">
                  Play a sound when notifications arrive
                </p>
              </div>
            </div>
            <Switch
              id="sound-enabled"
              checked={preferences.soundEnabled}
              onCheckedChange={(checked) => updatePreferences({ soundEnabled: checked })}
            />
          </div>

          <div className="border-t pt-6">
            <h3 className="font-medium mb-4">Notification Types</h3>
            <div className="space-y-4">
              {/* Chat Messages */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-5 w-5 text-blue-500" />
                  <div>
                    <Label htmlFor="chat-messages" className="font-medium">
                      Chat Messages
                    </Label>
                    <p className="text-sm text-gray-500">
                      New messages in team chat
                    </p>
                  </div>
                </div>
                <Switch
                  id="chat-messages"
                  checked={preferences.chatMessages}
                  onCheckedChange={(checked) => updatePreferences({ chatMessages: checked })}
                  disabled={!preferences.pushEnabled}
                />
              </div>

              {/* Case Updates */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Briefcase className="h-5 w-5 text-amber-500" />
                  <div>
                    <Label htmlFor="case-updates" className="font-medium">
                      Case Updates
                    </Label>
                    <p className="text-sm text-gray-500">
                      Changes to cases you're assigned to
                    </p>
                  </div>
                </div>
                <Switch
                  id="case-updates"
                  checked={preferences.caseUpdates}
                  onCheckedChange={(checked) => updatePreferences({ caseUpdates: checked })}
                  disabled={!preferences.pushEnabled}
                />
              </div>

              {/* Task Reminders */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckSquare className="h-5 w-5 text-green-500" />
                  <div>
                    <Label htmlFor="task-reminders" className="font-medium">
                      Task Reminders
                    </Label>
                    <p className="text-sm text-gray-500">
                      Upcoming deadlines and task assignments
                    </p>
                  </div>
                </div>
                <Switch
                  id="task-reminders"
                  checked={preferences.taskReminders}
                  onCheckedChange={(checked) => updatePreferences({ taskReminders: checked })}
                  disabled={!preferences.pushEnabled}
                />
              </div>

              {/* System Alerts */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <div>
                    <Label htmlFor="system-alerts" className="font-medium">
                      System Alerts
                    </Label>
                    <p className="text-sm text-gray-500">
                      Important system notifications
                    </p>
                  </div>
                </div>
                <Switch
                  id="system-alerts"
                  checked={preferences.systemAlerts}
                  onCheckedChange={(checked) => updatePreferences({ systemAlerts: checked })}
                  disabled={!preferences.pushEnabled}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <p className="font-medium">About Push Notifications</p>
              <p className="text-sm text-gray-500 mt-1">
                Push notifications allow LEAP Pro to send you alerts even when you're not 
                actively using the app. You can customize which types of notifications you 
                receive and disable them at any time.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
